import Checkbox from '@/components/Checkbox';
import Kbd from '@/components/Kbd';
import KeyHandler from '@/components/KeyHandler';
import Link from '@/components/Link';
import Portal from '@/components/Portal';
import Tag from '@/components/Tag';
import { FiInfo } from '@/components/react-icons/main';
import IconV2 from '@/components/v2/IconV2';
import setPath from '@/lib/setPath';
import selectors from '@/redux/selectors/selectors';
import { autoUpdate, flip, offset, shift, useClick, useDismiss, useFloating, useInteractions, useRole } from '@floating-ui/react';
import { createRef, isValidElement, memo, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

const isBlacklisted = (e, blacklist) => {
  if (blacklist) {
    for (let i = 0; i < blacklist.length; i++) {
      const b = blacklist[i];
      if (e?.target?.matches(b) || e?.currentTarget?.matches(b)) {
        return true;
      }
    }
  }
  return false;
};

const DEFAULT_OPTION_ROW_HEIGHT = 29;
// Sub-label options render a second line (see .option.has-sub-label in
// floating-select-v2.scss: 45px + 1px margin). Virtualization positions rows
// at a fixed pitch, so this must match or taller rows overlap.
const SUB_LABEL_OPTION_ROW_HEIGHT = 46;
const OPTIONS_VIEWPORT_HEIGHT = 240;
const VIRTUAL_OVERSCAN = 8;

const FloatingSelectV2 = memo((props) => {
  const isMobile = useSelector(selectors.mobile);
  const [active, setActive] = useState(props.active === undefined ? false : props.active);
  const [trigger, setTrigger] = useState(undefined);
  const [search, setSearch] = useState('');
  const [optionsIndex, setOptionsIndex] = useState(props.noAutoFocus ? null : 0);
  const [optionsScrollTop, setOptionsScrollTop] = useState(0);

  const searchRef = useRef(null);
  const optionsRef = useRef(null);
  const optionsScrollRef = useRef(null);
  const alignElementRef = useRef(undefined);

  const id = useRef(`floating-select-${Math.floor(Math.random() * 100000)}`);

  const nullable = props.nullable === true;
  const isMultiple = props.multiple === true;
  const useNumbers = props.useNumbers === true;
  const hideCheckboxes = props.hideCheckboxes === true;
  const isActive = props.active !== undefined ? props.active : active;
  const allowScroll = props.allowScroll !== undefined ? props.allowScroll : true;
  const wrapperWidth = props.width || (props.matchTriggerWidth ? trigger?.offsetWidth : undefined);
  const wrapperHeight = props.height || 'auto';
  const isFullDisplay = props.fullDisplay && !search.trim();
  const loading = props.loading || false;
  const deferredSearch = useDeferredValue(search);
  const deferredCustomSearch = useDeferredValue(props.customSearch || '');

  const isNumbered = !!props.numbered;

  const values = useMemo(() => (Array.isArray(props.value) ? props.value : [props.value]), [props.value]);
  const activeOptions = useMemo(() => props.options?.filter((n) => (props.freeform ? !n.value || values.includes(n.value) : values.includes(n.value))) || [], [props.freeform, props.options, values]);

  const useSearch = !isMobile && props.useSearch !== false;

  const filteredOptions = useMemo(() => {
    if (!isActive) {
      return [];
    }

    let options = props.filteredOptions || props?.options || [];
    options = Array.isArray(options) ? options : [];

    if (deferredSearch && !props?.customSearch) {
      const normalizedSearch = String(deferredSearch).toLowerCase();
      options = options.filter((n) =>
        String(`${n?.searchText || ''} ${n?.label} ${n?.value}`)
          .toLowerCase()
          .includes(normalizedSearch)
      );
    }

    if (deferredCustomSearch) {
      const normalizedCustomSearch = String(deferredCustomSearch).toLowerCase();
      options = options.filter((n) =>
        String(`${n?.searchText || ''} ${n?.name} ${n?.label} ${n?.value}`)
          .toLowerCase()
          .includes(normalizedCustomSearch)
      );
    }

    if (nullable && !deferredSearch && !deferredCustomSearch) {
      return [{ label: props?.nullableLabel || 'None', value: null }, ...options];
    }

    return options;
  }, [deferredCustomSearch, deferredSearch, isActive, nullable, props.filteredOptions, props.options, props.customSearch, props.nullableLabel]);

  const optionRefList = useMemo(() => (isFullDisplay ? filteredOptions.map(() => createRef()) : []), [filteredOptions.length, isFullDisplay]);
  const hasSubLabelOption = useMemo(() => filteredOptions.some((option) => option?.subLabel), [filteredOptions]);
  const optionRowHeight = props.optionRowHeight || (hasSubLabelOption ? SUB_LABEL_OPTION_ROW_HEIGHT : DEFAULT_OPTION_ROW_HEIGHT);
  const hasNestedOptions = filteredOptions.some((option) => option?.children?.length > 0);
  const shouldVirtualizeOptions = !isFullDisplay && !hasNestedOptions && props.virtualize !== false;
  const visibleStartIndex = shouldVirtualizeOptions ? Math.max(0, Math.floor(optionsScrollTop / optionRowHeight) - VIRTUAL_OVERSCAN) : 0;
  const visibleEndIndex = shouldVirtualizeOptions ? Math.min(filteredOptions.length, Math.ceil((optionsScrollTop + OPTIONS_VIEWPORT_HEIGHT) / optionRowHeight) + VIRTUAL_OVERSCAN) : filteredOptions.length;
  const virtualOptions = useMemo(() => (shouldVirtualizeOptions ? filteredOptions.slice(visibleStartIndex, visibleEndIndex) : filteredOptions), [filteredOptions, shouldVirtualizeOptions, visibleEndIndex, visibleStartIndex]);

  const hasNoOptions = !props.options || props.options.length === 0;

  const proxySetActive = (state) => {
    if (props.setActive !== undefined) {
      props.setActive(state);
      return;
    }
    setActive(state);
    if (!state) {
      props?.onHide?.();
    }
  };

  // Floating UI stuff

  const { refs, floatingStyles, context } = useFloating({
    open: isActive,
    onOpenChange: proxySetActive,
    middleware: [offset({ mainAxis: props.offset || 0, crossAxis: props.crossAxisOffset || 0 }), flip(), shift()],
    placement: props.placement || 'bottom-start',
    strategy: props.strategy || 'fixed',
    whileElementsMounted: autoUpdate,
    elements: {
      reference: alignElementRef.current || trigger
    },
    transform: false
  });

  const click = useClick(context);
  const dismiss = useDismiss(context, { outsidePress: (e) => !e.target.closest('.workflow-input-variable-pane') });
  const role = useRole(context);

  const { getFloatingProps } = useInteractions([click, dismiss, role]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    props?.onSearch?.(e.target.value);
    setOptionsIndex(0);
    setOptionsScrollTop(0);
    if (optionsScrollRef.current) {
      optionsScrollRef.current.scrollTop = 0;
    }
  };

  const handleOptionsScroll = (e) => {
    setOptionsScrollTop(e.currentTarget.scrollTop);
  };

  const handleSearchKeyDown = (e) => {
    if (useNumbers && !isNaN(e.key)) {
      const option = filteredOptions[parseFloat(e.key) - 1];
      if (option) {
        handleOptionClick(option, e);
      }
    }
  };

  const handleCheckboxClick = (option, e) => {
    e.stopPropagation();
    e.preventDefault();
    const onChange = props.onChange || (() => {});
    const newOptions = activeOptions.find((n) => n.value === option.value) ? [...activeOptions.filter((n) => (props.freeform ? !n.value || n.value !== option.value : n.value !== option.value))] : [...activeOptions, option];
    onChange(newOptions.map((n) => n?.value));
  };

  const handleOptionClick = (option, e, opts = {}) => {
    const pathMatches = window?.location?.pathname === option?.route;
    if (option?.route && !pathMatches) {
      if (e?.metaKey) {
        e.stopPropagation();
        e.preventDefault();
        window.open(option.route, '_blank');
      }
      if (opts?.forceRoute) {
        setPath(option.route);
      }
      proxySetActive(false, e, 'handleOptionClick');
      return;
    }
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    const onChange = option?.onChange || props?.onChange || (() => {});
    setSearch('');
    setOptionsIndex(0);
    if (!isMultiple) {
      proxySetActive(false, e, 'handleOptionClick');
    }
    if (!props.multiple) {
      return onChange(option, e);
    }
    const newOptions = activeOptions.find((n) => n.value === option.value) ? [...activeOptions.filter((n) => (props.freeform ? !n.value || n.value !== option.value : n.value !== option.value))] : [...activeOptions, option];
    onChange(newOptions.map((n) => n?.value));
    setTimeout(() => {
      if (searchRef.current) {
        searchRef.current.focus();
      } else if (optionsRef.current) {
        optionsRef.current.focus();
      }
    }, 50);
  };

  // keyMap Stuff
  const handleKeyDown = (e) => {
    const indexIsNull = optionsIndex === null;
    const maxIndex = filteredOptions.length - 1;

    // Check for custom shortcuts first
    const matchingOption = filteredOptions.find((option) => option?.shortcut && option.shortcut.key === e.key);
    if (matchingOption && isActive) {
      e.preventDefault();
      e.stopPropagation();
      handleOptionClick(matchingOption, e);
      proxySetActive(false);
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        if (indexIsNull) {
          setOptionsIndex(0);
          return;
        }
        setOptionsIndex((prevIndex) => (prevIndex < maxIndex ? prevIndex + 1 : prevIndex));
        props?.keyMap?.ArrowDown?.(e);
        break;
      case 'ArrowUp':
        if (indexIsNull) {
          setOptionsIndex(maxIndex);
          return;
        }
        setOptionsIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : prevIndex));
        props?.keyMap?.ArrowUp?.(e);
        break;
      case 'Home':
        setOptionsIndex(0);
        props?.keyMap?.Home?.(e);
        break;
      case 'End':
        setOptionsIndex(maxIndex);
        props?.keyMap?.End?.(e);
        break;
      case 'PageDown':
        if (indexIsNull) {
          setOptionsIndex(7);
          return;
        }
        setOptionsIndex((prevIndex) => Math.min(prevIndex + 7, maxIndex));
        props?.keyMap?.PageDown?.(e);
        break;
      case 'PageUp':
        if (indexIsNull) {
          setOptionsIndex(maxIndex - 7);
          return;
        }
        setOptionsIndex((prevIndex) => Math.max(prevIndex - 7, 0));
        props?.keyMap?.PageUp?.(e);
        break;
      case 'Enter':
        setSearch('');
        handleOptionClick(filteredOptions[optionsIndex], e, { forceRoute: true });
        props?.keyMap?.Enter?.(e);
        break;
      case 'Escape':
        setSearch('');
        proxySetActive(false, e, 'handleSearchKeyUp');
        props?.keyMap?.Escape?.(e);
        break;
      default:
        break;
    }
  };

  const shortcutKeyMap = filteredOptions.reduce((acc, option) => {
    const key = option?.shortcut?.key;
    if (key) {
      acc[key] = handleKeyDown;
    }
    return acc;
  }, {});

  const keyMap = {
    ArrowDown: handleKeyDown,
    ArrowUp: handleKeyDown,
    Home: handleKeyDown,
    End: handleKeyDown,
    PageDown: handleKeyDown,
    PageUp: handleKeyDown,
    Delete: handleKeyDown,
    Enter: handleKeyDown,
    ...shortcutKeyMap
  };

  useEffect(() => {
    if (props.alignWith) {
      alignElementRef.current = document.querySelector(props.alignWith);
    } else {
      alignElementRef.current = undefined;
    }
  }, [props.alignWith, isActive]);

  useEffect(() => {
    if (props.noAutoFocus) {
      return;
    }

    if (isActive) {
      const focusTimeoutId = setTimeout(() => {
        if (searchRef.current) {
          searchRef.current.focus();
        } else if (optionsRef.current) {
          optionsRef.current.focus();
        }
      }, 50);

      return () => clearTimeout(focusTimeoutId);
    } else {
      setOptionsIndex(0);
      setOptionsScrollTop(0);
    }
  }, [isActive]);

  useEffect(() => {
    const handleTriggerClick = (e) => {
      if (isBlacklisted(e, props.blacklist)) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      setTrigger(e.currentTarget || e.target);
      proxySetActive(true, e, 'handleTriggerClick');
    };
    const handleTriggerFocus = (e) => {
      setTrigger(e.currentTarget || e.target);
    };
    const retryTimeoutIds = [];
    let mounted = true;
    const attachListenersToElements = (elements, method, handleClick, handleFocus) => {
      if (!elements || elements.length === 0) {
        return;
      }
      if (!props.disableClickTrigger) {
        elements.forEach((el) => el[method]('click', handleClick));
      }
      elements.forEach((el) => el[method]('focus', handleFocus));
    };
    const attachListenersToSelector = (selector, method, handleClick, handleFocus) => {
      if (method === 'removeEventListener') {
        attachListenersToElements(Array.from(document.querySelectorAll(selector)).filter(Boolean), method, handleClick, handleFocus);
        return;
      }

      let attempts = 0;
      const maxAttempts = 3;
      const tryAttach = () => {
        if (!mounted) {
          return;
        }

        const els = Array.from(document.querySelectorAll(selector));
        if (els.length > 0 || ++attempts > maxAttempts) {
          attachListenersToElements(els.filter(Boolean), method, handleClick, handleFocus);
        } else {
          retryTimeoutIds.push(setTimeout(tryAttach, 60));
        }
      };
      tryAttach();
    };
    const setListeners = (triggers = [], action = 'add') => {
      if (!triggers || triggers.length === 0) {
        return;
      }
      const method = action === 'add' ? 'addEventListener' : 'removeEventListener';
      triggers.filter(Boolean).forEach((n) => {
        if (typeof n === 'string') {
          attachListenersToSelector(n, method, handleTriggerClick, handleTriggerFocus);
        } else {
          const ref = typeof n === 'object' ? n?.current : n;
          attachListenersToElements([ref].filter(Boolean), method, handleTriggerClick, handleTriggerFocus);
        }
      });
    };
    setListeners(props.triggers, 'add');
    return () => {
      mounted = false;
      retryTimeoutIds.forEach(clearTimeout);
      setListeners(props.triggers, 'remove');
    };
  }, [props.triggers, props.disableClickTrigger]);

  useEffect(() => {
    const searchElement = typeof props.search === 'string' ? document.querySelector(props.search) : props.search;
    if (searchElement) {
      searchElement.addEventListener('keyup', handleSearchChange);
    }
    return () => {
      if (searchElement) {
        searchElement.removeEventListener('keyup', handleSearchChange);
      }
    };
  }, [search]);

  useEffect(() => {
    if (optionsIndex === null) {
      return;
    }

    if (shouldVirtualizeOptions) {
      if (optionsScrollRef.current) {
        const optionTop = optionsIndex * optionRowHeight;
        const optionBottom = optionTop + optionRowHeight;
        const visibleTop = optionsScrollRef.current.scrollTop;
        const visibleBottom = visibleTop + OPTIONS_VIEWPORT_HEIGHT;

        if (optionTop < visibleTop) {
          optionsScrollRef.current.scrollTop = optionTop;
          setOptionsScrollTop(optionTop);
        }

        if (optionBottom > visibleBottom) {
          const nextScrollTop = optionBottom - OPTIONS_VIEWPORT_HEIGHT;
          optionsScrollRef.current.scrollTop = nextScrollTop;
          setOptionsScrollTop(nextScrollTop);
        }
      }
      return;
    }

    if (!isFullDisplay) {
      return;
    }

    if (optionRefList[optionsIndex]?.current) {
      optionRefList[optionsIndex].current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }, [isFullDisplay, optionRefList, optionRowHeight, optionsIndex, shouldVirtualizeOptions]);

  if (!isActive) {
    return null;
  }

  /**
   * Get the label to render for an option. Adds an incremented prefix if numbered prop is true.
   */
  const getRenderedOptionLabel = (label, idx) => {
    if (isNumbered) {
      // Custom: numbered prop
      // If label is a function, we just return as is (for now), otherwise prepend index+1.
      if (typeof label === 'function') {
        return (
          <>
            {`${idx + 1}. `}
            {label()}
          </>
        );
      }
      return `${idx + 1}. ${label}`;
    } else {
      if (typeof label === 'function') {
        return label();
      }
      return label;
    }
  };

  const renderOption = (option, idx) => {
    const optionLabel = option?.label || option?.value;
    const icon = !isValidElement(option?.icon) ? option?.icon : undefined;
    const reactIcon = isValidElement(option?.icon) ? option.icon : undefined;

    const labelContent = (
      <>
        {getRenderedOptionLabel(optionLabel, idx)}
        {option.totalCount !== undefined && option.totalCount !== null && <Tag data-title={option.totalCountTitle}>{option.totalCount}</Tag>}
        {option?.tag && <Tag {...(option?.tagProps || {})}>{option.tag}</Tag>}
      </>
    );

    return (
      <Link to={option?.route} target={option?.target} key={idx}>
        <div id={`${id.current}-option-${idx}`} className={`option ${isMultiple ? 'multiple' : ''} ${option?.subLabel ? 'has-sub-label' : ''} ${values.includes(option.value) || values.includes(optionLabel) ? 'active' : ''}${idx === optionsIndex ? ' highlighted' : ''}`} onClick={(e) => handleOptionClick(option, e)}>
          <div className={`option-inner-wrapper`}>
            {isMultiple && !hideCheckboxes && <Checkbox active={values?.includes(option?.value)} onClick={(e) => handleCheckboxClick(option, e)} />}
            {typeof icon === 'string' && (
              <IconV2 {...(option?.iconProps || {})}>
                <img alt="Icon" src={icon} />
              </IconV2>
            )}
            {typeof icon !== 'string' && icon && <IconV2 icon={icon} {...(option?.iconProps || {})} />}
            {reactIcon && <IconV2 {...(option?.iconProps || {})}>{reactIcon}</IconV2>}
            <div className={`label ${option?.subLabel ? 'has-sub-label' : ''}`} data-title={optionLabel}>
              {option?.subLabel ? (
                <>
                  <div className={'label-main'}>{labelContent}</div>
                  <div className={'option-sub-label'} data-title={option.subLabel}>
                    {option.subLabel}
                  </div>
                </>
              ) : (
                labelContent
              )}
            </div>
            <div className="option-right">
              {useNumbers && <div className={'right number'}>{idx + 1}</div>}
              {option?.shortcut && <Kbd>{option?.shortcut?.label}</Kbd>}
              {option?.right && <div className={'right'}>{option?.right}</div>}
              {option?.help && <IconV2 className={'light-medium help'} icon={FiInfo} data-title={option?.help} />}
            </div>
          </div>
          {option?.children?.length > 0 && <FloatingSelectV2 triggers={[`#${id.current}-option-${idx}`]} options={option.children} onChange={props.onChange} useSearch={props.useChildrenSearch} onSearch={props.onSearch} width={wrapperWidth} parentId={id.current} placement={'right-start'} multiple={props.multiple} value={props.value} numbered={props.numbered} optionRowHeight={props.optionRowHeight} />}
        </div>
      </Link>
    );
  };

  const renderFullDisplayOption = (option, idx) => {
    const optionLabel = option?.label || option?.value;
    const icon = !isValidElement(option?.icon) ? option?.icon : undefined;
    const reactIcon = isValidElement(option?.icon) ? option.icon : undefined;

    return (
      <Link key={idx} to={option?.route} target={option?.target}>
        <div id={`${id.current}-option-${idx}`} className={`option ${isMultiple ? 'multiple' : ''} ${values.includes(option.value) || values.includes(optionLabel) ? 'active' : ''}${idx === optionsIndex ? ' highlighted' : ''}`} onClick={(e) => handleOptionClick(option, e)} ref={optionRefList[idx]}>
          <div className="option-inner-wrapper">
            {isMultiple && <Checkbox active={values?.includes(option?.value)} onClick={(e) => handleCheckboxClick(option, e)} />}
            {typeof icon === 'string' && (
              <IconV2 {...(option?.iconProps || {})}>
                <img alt="Icon" src={icon} />
              </IconV2>
            )}
            {typeof icon !== 'string' && icon && <IconV2 icon={icon} {...(option?.iconProps || {})} />}
            {reactIcon && <IconV2 {...(option?.iconProps || {})}>{reactIcon}</IconV2>}
            <div className={'label'}>
              {getRenderedOptionLabel(optionLabel, idx)}
              {option.totalCount !== undefined && option.totalCount !== null && <Tag>{option.totalCount}</Tag>}
              {option?.tag && <Tag {...(option?.tagProps || {})}>{option.tag}</Tag>}
            </div>
            <div className="option-right" style={{ position: 'relative' }}>
              {useNumbers && <div className={'right'}>{idx + 1}</div>}
              {option?.shortcut && <Kbd>{option?.shortcut?.label}</Kbd>}
              {option?.help && <IconV2 className={'light-medium help'} icon={FiInfo} data-title={option?.help} />}
            </div>
          </div>
          {option?.children?.length > 0 && <FloatingSelectV2 triggers={[`#${id.current}-option-${idx}`]} options={option.children} onChange={props.onChange} useSearch={props.useChildrenSearch} onSearch={props.onSearch} width={wrapperWidth} parentId={id.current} placement={'right-start'} numbered={props.numbered} />}
        </div>
      </Link>
    );
  };

  return (
    <Portal id={props.parentId || id.current}>
      <KeyHandler keyMap={keyMap} stopPropagation={true} preventDefault={true} target={useSearch ? searchRef : optionsRef} />
      <div className={`comp-floating-select-v2 ${props.className || ''} ${allowScroll ? 'scroll' : 'no-scroll'}`}>
        {isMobile && <div className={`backing`} />}
        <div className={`floating-select`} ref={refs.setFloating} style={floatingStyles} {...getFloatingProps()}>
          <div className={`option-wrapper ${isFullDisplay ? 'full-display' : ''}`} style={{ width: wrapperWidth, height: wrapperHeight, minWidth: props.minWidth }} ref={optionsRef} tabIndex={-1} onFocus={props.noAutoFocus ? () => setOptionsIndex(0) : undefined} onBlur={props.noAutoFocus ? () => setOptionsIndex(null) : undefined}>
            {useSearch && !props.search && (
              <div className={'search-wrapper'}>
                <input placeholder={props.placeholder || 'Search'} onChange={handleSearchChange} ref={searchRef} value={search || ''} onKeyDown={handleSearchKeyDown} />
              </div>
            )}
            {loading && <div className="loading-options">Fetching results...</div>}
            {!loading && hasNoOptions && <div className="empty-options">No options available</div>}
            {!loading && !hasNoOptions && filteredOptions?.length > 0 && (
              <div className={`options scrollbar-light ${props.smartWidth ? 'smart-width' : ''}  ${isFullDisplay ? 'full-display' : ''}`} ref={optionsScrollRef} onScroll={handleOptionsScroll}>
                {isFullDisplay ? (
                  props.fullDisplay.layout?.map((columns, rowIndex) => {
                    const startIdx = props.fullDisplay.layout.slice(0, rowIndex).reduce((sum, cols) => sum + cols, 0);
                    const rowOptions = filteredOptions.slice(startIdx, startIdx + columns);
                    if (rowOptions.length === 0) return null;

                    return (
                      <div key={rowIndex} className="options-row" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
                        {rowOptions.map((option, idx) => renderFullDisplayOption(option, startIdx + idx))}
                      </div>
                    );
                  })
                ) : shouldVirtualizeOptions ? (
                  <div className="virtual-options" style={{ height: filteredOptions.length * optionRowHeight }}>
                    {virtualOptions.map((option, idx) => {
                      const optionIndex = visibleStartIndex + idx;
                      return (
                        <div key={`${option?.value ?? 'option'}-${optionIndex}`} className="virtual-option" style={{ transform: `translateY(${optionIndex * optionRowHeight}px)` }}>
                          {renderOption(option, optionIndex)}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  filteredOptions.map((option, idx) => renderOption(option, idx))
                )}
              </div>
            )}
          </div>
        </div>
        <div className={`floating-select-placeholder`} />
      </div>
    </Portal>
  );
});

FloatingSelectV2.displayName = 'FloatingSelectV2';

export default FloatingSelectV2;
